import React from 'react';
import { get, noop } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { lifecycle, withHandlers, withState } from 'recompose';

import withBaseUrl from '../../hoc/withBaseUrl';
import { getOwnerTypeAndId } from '../../../auth/selectors';
import withNotifications from '../../../common/withNotifications';
import {
  copyBrandMembership,
  copyShopMembership,
  archiveBrandMembership,
  archiveShopMembership,
} from '../actions';
import withOwnerTypeAndId from '../../../common/hoc/withOwnerTypeAndId';
import { bindActionsForRoles } from '../../../common/helpers/actions';
import { withPaginatedStoreHelpers, createPaginatedReduxStore } from '../../../common/paginatedReduxStore';
import confirmModal from '../../../common/confirmModal';

export const MEMBERSHIPS_LIST_PAGE_SIZE = 20;

const storeControls = createPaginatedReduxStore({
  namespace: 'memberships',
  buildUrl: (state) => {
    const owner = getOwnerTypeAndId(state);
    return `/${owner.type}/${owner.id}/memberships`;
  },
  limit: MEMBERSHIPS_LIST_PAGE_SIZE,
  httpVersion: 1,
});

export default compose(
  withNotifications,
  withPaginatedStoreHelpers(storeControls),
  withRouter,
  withBaseUrl,
  withOwnerTypeAndId,
  connect(
    null,
    dispatch => ({
      copyMembership: bindActionsForRoles(dispatch, copyShopMembership, noop, copyBrandMembership),
      archiveMembership: bindActionsForRoles(dispatch, archiveShopMembership, noop, archiveBrandMembership),
    }),
  ),
  withHandlers({
    addMembership: props => () => {
      props.router.push(`${props.baseUrl}/add`);
    },
    importSubscriptions: props => () => {
      props.router.push(`${props.baseUrl}/import`);
    },
  }),
  lifecycle({
    componentDidMount() {
      this.props.openPage(1);
    },
  }),
  withState('pendingMemberships', 'setPendingMemberships', {}),
  withHandlers({
    viewMembership: props => (e, membership) => {
      e.stopPropagation();
      props.router.push(`${props.baseUrl}/${membership.id}/view`);
    },
    editMembership: props => (e, membership) => {
      e.stopPropagation();
      props.router.push(`${props.baseUrl}/${membership.id}/edit`);
    },
    copyMembership: props => async (e, membership) => {
      e.stopPropagation();

      props.setPendingMemberships({
        ...props.pendingMemberships,
        [membership.id]: true,
      });

      try {
        await props.copyMembership[props.ownerType](props.ownerId, membership);
        await props.refreshPageData();
      } finally {
        props.setPendingMemberships({
          ...props.pendingMemberships,
          [membership.id]: false,
        });
      }

      props.addNotification({
        title: 'Success',
        level: 'success',
        message: 'Membership copied!',
      });
    },
    archiveMembership: props => async (e, membership) => {
      e.stopPropagation();

      if (membership.counts.active > 0) {
        props.addNotification({
          title: 'Can\'t archive membership',
          level: 'warning',
          message: `This membership has ${membership.counts.active} active members and can’t be archived.`,
        });

        return;
      }

      const confirmed = await confirmModal({
        type: 'danger',
        title: 'Archive membership package',
        message: (
          <span className="u-fontsize-normal">
            Are you sure you want to archive this membership package?<br />
            This won’t delete the package, but will make it inactive.
          </span>
        ),
        confirmButtonText: 'Archive',
        negativeButtonText: 'Cancel',
      });

      if (!confirmed) {
        return;
      }

      await props.archiveMembership[props.ownerType](props.ownerId, membership);

      props.refreshPageData();

      props.addNotification({
        title: 'Success',
        level: 'success',
        message: 'Membership archived!',
      });
    },
  }),
  withHandlers({
    canEditMembership: props => (membership) => {
      if (props.ownerType === 'brand') {
        return true;
      }

      const ownerAssignment = membership.assignees.find(assignee => assignee.assigneeId === props.ownerId);
      return get(ownerAssignment, 'isCreator', false);
    },
    canViewMembershipAssignees: props => () => {
      return props.ownerType === 'brand';
    },
    canCopyMembership: () => () => {
      return true;
    },
    canArchiveMembership: props => () => {
      return props.ownerType === 'brand';
    },
    canViewStats: props => () => {
      return props.ownerType === 'shop';
    },
  }),
  withHandlers({
    canPerformActions: props => (membership) => {
      return (
        props.canViewMembershipAssignees(membership)
        || props.canEditMembership(membership)
        || props.canCopyMembership(membership)
      );
    },
  }),
);
